import { useCallback, useEffect } from 'react'
import { ModalEditComponentProps } from '../../../../../../../../../../../baseComponents/ModalsLayouts/types'
import twinFetchPost from '../../../../../../../../../../../utils/globals/data'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { changeErrorMessage } from '../../../../../../../../../../../utils/reducers/reduxDispatch'
import { getConfigParam, getPreparePricesConfigParam, getUserPermissions } from '../../../../../../../../../../../utils/reducers/getters'
import { permissionCheck } from '../../../../../../../../../../../baseComponents/PermissionChecker/function'
import { SetEditDeleteModalTy } from '../../../../../../../../../../../utils/hooks/useEditDeleteModal'
import useIsLoading from '../../../../../../../../../../../utils/hooks/useIsLoading'
import { parseOrderTableData } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderQuoteLayout/function'
import { HeaderDataCalculate } from '@teinor/erp/types/company/order/general'
import { CalculateParamsTy } from '../../../../../../../../../../../baseComponents/Displays/DisplayPrice/types'
import { OrderData } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import { TwinDictionary } from '../../../../../../../../../../../utils/globals/dictionary'
import ModalSmallFilled from '../../../../../../../../../../../baseComponents/ModalsLayouts/ModalSmallFilled'

interface ModalFinalizeSellInvoiceProps extends ModalFinalizeSellInvoiceLogicProps {
}

const ModalFinalizeSellInvoice: React.FC<ModalFinalizeSellInvoiceProps> = ({ setOpenModal, onSubmit, allRowData, url, ...rest }) => {
    const { t } = useTwinTranslation()
    const { finalizeOrder, loading } = useModalFinalizeSellInvoiceLogic({ setOpenModal, onSubmit, allRowData, url, ...rest })
    return (
        <ModalSmallFilled opened={true} setOpened={rest.setOpened} onCancel={() => setOpenModal(null)} onAccept={() => finalizeOrder(url)} translations={{
            title: t('sureProcessInvoice', '¿Seguro que quieres marcar como facturado esta factura?'),
            subtitle: t('onceProcessedNoMoreChanges', 'Una vez procesada no podrás modificarla'),
            buttonAccept: t('accept', 'Aceptar'),
        }} loading={loading} />
    )
}

interface ModalFinalizeSellInvoiceLogicProps extends ModalEditComponentProps {
    url: string
    data: OrderData
    setOpenModal: SetEditDeleteModalTy
}

const useModalFinalizeSellInvoiceLogic = ({ onSubmit, allRowData, setOpenModal, data, url}: ModalFinalizeSellInvoiceLogicProps) => {
    const { t } = useTwinTranslation()
    const errorMsg = t('noProductStockOnLine', 'No hay stock del producto en la línea')
    const { startLoading, endLoading, loading } = useIsLoading()

    const finalizeOrder = useCallback(async (url: string, _navigateUrl?: string, extraProps?: TwinDictionary) => {
        startLoading?.()
        const response = await twinFetchPost(url, { id: allRowData?.id, status: allRowData?.status, ...extraProps })
        const responseJSON = await response.json()
        if (response.status === 200) {
            setOpenModal(null)
            onSubmit(response, {})
        } else {
            if (responseJSON.errors[0].msg.includes('noStock')) {
                const skipStock = permissionCheck(getUserPermissions('sellOther.sellWithoutStock'), 'read')
                if (skipStock) {
                    setOpenModal({ type: 'edit', allRowData: { extraProps: { ...allRowData, OrderId: allRowData?.id, skipCheckOperateStock: true }, createUrl: url, onSubmit, data, finalizeOrder } })
                } else {
                    const msgArr = responseJSON.errors[0].msg.split('-')
                    let myMsg = errorMsg + ' ' + msgArr[1]
                    if (msgArr[2]) {
                        myMsg += ' nº: ' + msgArr[2]
                    }
                    changeErrorMessage(myMsg)
                    setOpenModal(null)
                }
            }
        }
        endLoading?.()
    }, [setOpenModal, allRowData, onSubmit, errorMsg, startLoading, endLoading, data])

    const checkOpenSellInvoicePaymentM = useCallback(() => {
        const sellInvoicePaymentMethodMandatory = getConfigParam('sellInvoicePaymentMethodMandatory')
        if (sellInvoicePaymentMethodMandatory === 'on') {
            const calculateParams = getPreparePricesConfigParam() as CalculateParamsTy
            const headerDataCalculate: HeaderDataCalculate = {
                OrderCosts: data?.header?.OrderCosts,
                ParamsRoundingDecimals: calculateParams,
                total_recharge_after: data?.header?.total_recharge_after,
                OrderShipping: data?.header?.OrderShipping,
                isFinished: data?.header?.isFinished,
                OrderAccounts: data?.header?.OrderAccounts
            }
            const totalsOrderTableData = parseOrderTableData(data?.tableData as any, headerDataCalculate)
            const paymentTotal = parseFloat(String(data?.header?.PayMCopy?.total_total || '0'))
            const tableDataTotal = parseFloat(String(totalsOrderTableData.totals.total))
            if (paymentTotal !== tableDataTotal) {
                setOpenModal({ type: 'delete', allRowData: { totalsOrderTableData, onSubmit: () => finalizeOrder(url, '') } })
            }
        }
    }, [data, finalizeOrder, setOpenModal, url])

    useEffect(() => {
        checkOpenSellInvoicePaymentM()
    }, [checkOpenSellInvoicePaymentM])
    
    return { finalizeOrder, loading }
}


export default ModalFinalizeSellInvoice